(function () {
    // The drawer lives in the header group, so its section ID is dynamic
    // (sections--<group>__<key>). Requesting it by file name renders a fresh
    // copy with default settings instead of the configured (and per-market) ones.
    const DRAWER_SECTION_HANDLE = 'mm-cart-drawer';

    function getCartSections() {
      const drawerId = getCartDrawer()?.dataset.sectionId || DRAWER_SECTION_HANDLE;
      return [drawerId, 'cart-icon-bubble'];
    }
  
    const routes = window.theme?.routes || window.routes;
    const fetchConfig = window.theme?.utils?.fetchConfig || window.fetchConfig;
  
    let pendingDiscountCode = null;
  
    const ATC_LOADING_CLASS = 'mm-atc-loading';
  
    function setCartAddLoading(form, active) {
      if (!form || typeof form.classList === 'undefined') return;
      form.classList.toggle(ATC_LOADING_CLASS, !!active);
      const submitters = form.querySelectorAll('button[type="submit"], input[type="submit"], button[name="add"]');
      submitters.forEach((btn) => {
        if (active) {
          btn.setAttribute('aria-busy', 'true');
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('loading');
        } else {
          btn.removeAttribute('aria-busy');
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('loading');
        }
        const spinner = btn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !active);
      });
    }
  
    function getCartDrawer() {
      return document.querySelector('mm-cart-drawer');
    }
  
    function getDiscountCodeFromForm(form) {
      if (!form || typeof form.closest !== 'function') return null;
      const host = form.closest('[data-discount-code]');
      if (!host) return null;
      const code = host.getAttribute('data-discount-code');
      return code && code.trim() ? code.trim() : null;
    }
  
    // Adds the drawer + icon bubble to whatever sections the caller asked for,
    // swapping any request for the drawer by file name for its real section ID.
    function mergeSections(existing) {
      const list = (Array.isArray(existing) ? existing : String(existing || '').split(','))
        .map((s) => String(s).trim())
        .filter((s) => s && s !== DRAWER_SECTION_HANDLE);
      getCartSections().forEach((s) => { if (!list.includes(s)) list.push(s); });
      return list;
    }

    function injectSections(body) {
      try {
        if (typeof body === 'string') {
          const data = JSON.parse(body);
          data.sections = mergeSections(data.sections).join(',');
          return JSON.stringify(data);
        }
        if (body instanceof FormData || body instanceof URLSearchParams) {
          body.set('sections', mergeSections(body.get('sections')).join(','));
          return body;
        }
      } catch (_) { /* can't parse — pass through */ }
      return body;
    }

    function buildCartItem(variantId, quantity, sellingPlan, properties) {
      const item = {
        id: Number(variantId),
        quantity: Number(quantity) || 1,
      };
      if (sellingPlan) {
        item.selling_plan = Number(sellingPlan);
      }
      if (properties && Object.keys(properties).length) {
        item.properties = properties;
      }
      return item;
    }

    // Must match mmx-featured-product variant_picker_layout value `trial_dual_pack`.
    const TRIAL_DUAL_PACK_LAYOUT = 'trial_dual_pack';

    function buildDualPackProperties(formData, groupId) {
      const size1 = formData.get('dual_pack_size_1') || '';
      const size2 = formData.get('dual_pack_size_2') || '';

      return {
        _dual_pack_layout: TRIAL_DUAL_PACK_LAYOUT,
        _dual_pack_group: groupId,
        _dual_pack_size_1: size1,
        _dual_pack_size_2: size2,
      };
    }

    function isTrialDualPackForm(form, formData) {
      if (form.getAttribute('data-dual-pack-layout') !== TRIAL_DUAL_PACK_LAYOUT) {
        return false;
      }
      return Boolean(formData.get('pack_1_variant_id') && formData.get('pack_2_variant_id'));
    }

    function buildCartAddPayloadFromForm(form, formData) {
      const data = Object.fromEntries(formData.entries());
      data.sections = getCartSections().join(',');

      const pack1 = formData.get('pack_1_variant_id');
      const pack2 = formData.get('pack_2_variant_id');

      if (isTrialDualPackForm(form, formData)) {
        const sellingPlan = formData.get('selling_plan') || '';
        const payload = { sections: data.sections };
        const groupId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `dual-pack-${Date.now()}`;
        const baseProps = buildDualPackProperties(formData, groupId);

        if (String(pack1) === String(pack2)) {
          payload.items = [
            buildCartItem(pack1, 2, sellingPlan, {
              ...baseProps,
              _dual_pack_primary: 'true',
            }),
          ];
        } else {
          payload.items = [
            buildCartItem(pack1, 1, sellingPlan, {
              ...baseProps,
              _dual_pack_primary: 'true',
              _dual_pack_line: '1',
            }),
            buildCartItem(pack2, 1, sellingPlan, {
              ...baseProps,
              _dual_pack_line: '2',
            }),
          ];
        }

        return payload;
      }

      return data;
    }

    async function submitCartAddForm(form) {
      setCartAddLoading(form, true);
      try {
        pendingDiscountCode = getDiscountCodeFromForm(form);

        const formData = new FormData(form);
        const data = buildCartAddPayloadFromForm(form, formData);

        const addUrl = form.action.endsWith('.js') ? form.action : form.action + '.js';

        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const errText = await response.text().catch(() => '');
          console.error('[mm-cart-intercept] Cart add failed:', response.status, errText);
          return;
        }

        const parsed = await response.json();
        if (parsed.status) {
          console.error('[mm-cart-intercept] Cart add error:', parsed.description || parsed.message);
          return;
        }

        await onCartAddSuccess(parsed);
      } catch (err) {
        console.error('[mm-cart-intercept] Form submit error:', err);
      } finally {
        setCartAddLoading(form, false);
      }
    }
  
    async function applyDiscountCode(code, cartJson) {
      if (!code) return null;
  
      if (cartJson && Array.isArray(cartJson.discount_codes)
          && cartJson.discount_codes.some(dc => dc.code === code && dc.applicable !== false)) {
        return null;
      }
  
  
      fetch('/discount/' + encodeURIComponent(code), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        redirect: 'manual',
      }).catch(() => {});
  
      try {
        const body = JSON.stringify({
          discount: code,
          sections: getCartSections().join(','),
        });
  
        const response = await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body,
        });
        return await response.json();
      } catch (err) {
        console.error('[mm-cart-intercept] Discount apply error:', err);
        return null;
      }
    }
  
    async function onCartAddSuccess(parsedResponse) {
      try {
        const code = pendingDiscountCode;
        pendingDiscountCode = null;
  
        let cartJson = await (
          await fetch(routes.cart_url, { ...fetchConfig('json', 'GET') })
        ).json();
  
        if (code) {
          const discounted = await applyDiscountCode(code, cartJson);
          if (discounted) cartJson = discounted;
        }
  
        if (parsedResponse.sections && !cartJson.sections) {
          cartJson.sections = parsedResponse.sections;
        }
  
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-intercept',
          cart: cartJson,
        });

        const drawer = getCartDrawer();
        if (drawer && !drawer.classList.contains('active')) {
          drawer.show();
        }
      } catch (err) {
        console.error('[mm-cart-intercept] Error updating cart drawer:', err);
      }
    }
  
    const originalFetch = window.fetch;
  
    window.fetch = async function (...args) {
      const [resource, config] = args;
      const url = typeof resource === 'string' ? resource : resource?.url;
  
      if (url && url.includes('/cart/add')) {
        if (config?.body) config.body = injectSections(config.body);
  
        const response = await originalFetch.apply(this, args);
  
        if (response.ok) {
          const clone = response.clone();
          try {
            const parsed = await clone.json();
            await onCartAddSuccess(parsed);
          } catch (_) { /* non-JSON or post-add flow */ }
        }
  
        return response;
      }
  
      return originalFetch.apply(this, args);
    };
  
    document.addEventListener(
      'submit',
      function (event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.action?.includes('/cart/add')) return;
  
        event.preventDefault();
        event.stopPropagation();

        submitCartAddForm(form);
      },
      true
    );
  
    const originalSubmit = HTMLFormElement.prototype.submit;
  
    HTMLFormElement.prototype.submit = function () {
      if (this.action?.includes('/cart/add')) {
        submitCartAddForm(this);
        return;
      }
  
      return originalSubmit.apply(this, arguments);
    };
  })();
  